import { useParams, Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/data';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <h1 className="text-2xl font-heading text-foreground-900 mb-3">文章未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const category = categories.find((c) => c.slug === article.categorySlug);

  return (
    <>
      <SEOHead
        title={`${article.title} - caoliushequ官方导航`}
        description={article.summary}
        keywords={article.tags.join(',')}
        canonical={`https://nammage.cn/article/${article.id}`}
        ogTitle={article.title}
        ogDescription={article.summary}
        ogUrl={`https://nammage.cn/article/${article.id}`}
        ogType="article"
      />

      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.summary,
          datePublished: article.publishDate,
          author: { '@type': 'Person', name: article.author },
          publisher: { '@type': 'Organization', name: 'caoliushequ' },
          url: `https://nammage.cn/article/${article.id}`,
        }}
      />

      <div className="min-h-screen pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: article.category, to: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="max-w-4xl mx-auto px-4 md:px-6 py-10">
          <header className="mb-8">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <Link
                to={`/category/${article.categorySlug}`}
                className="text-xs px-3 py-1 rounded-full bg-primary-50 text-primary-600 hover:bg-primary-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                {article.category}
              </Link>
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs px-2 py-1 rounded-full bg-background-100 text-foreground-500">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
              <span className="flex items-center gap-1">
                <i className="ri-user-line" /> {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line" /> {article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line" /> {article.readCount.toLocaleString()} 阅读
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-chat-3-line" /> {article.commentCount.toLocaleString()} 评论
              </span>
            </div>
          </header>

          <div className="mb-10 rounded-xl overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              title={`${article.title} - caoliushequ`}
              className="w-full h-auto object-cover max-h-[420px]"
            />
          </div>

          <div
            className="prose prose-lg max-w-none text-foreground-700 leading-relaxed article-content"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-background-200/70">
            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs px-3 py-1.5 rounded-full bg-background-100 text-foreground-600">
                  # {tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 w-full px-4 md:px-6 py-14">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl font-heading text-foreground-900 mb-6">相关文章</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group bg-background-50 rounded-xl overflow-hidden hover:bg-background-200 transition-colors block cursor-pointer"
                  >
                    <div className="h-36 overflow-hidden">
                      <img
                        src={ra.imageUrl}
                        alt={ra.title}
                        title={`${ra.title} - caoliushequ`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-foreground-800 line-clamp-2 group-hover:text-primary-500 transition-colors">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-400 mt-2">{ra.publishDate}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
}